import { channelLabel, channelTableBadgeLabel } from './channelLabels';
import { OTA_RELEASE_CHANNEL_LADDER } from './channelLabels';
import { canDo, type UserRole } from './permissions';
import type { Build, ChannelPointer, OtaAppId, OtaReleaseChannelId } from './types';

export type PromoteActionKind = 'entry' | 'replace' | 'advance';

export interface PromoteTargetOption {
  channel: OtaReleaseChannelId;
  kind: PromoteActionKind;
  enabled: boolean;
  blockedReason?: string;
  currentVc: number | null;
  alreadyLive: boolean;
}

function pointerFor(
  pointers: ChannelPointer[],
  app: OtaAppId,
  channel: OtaReleaseChannelId,
): ChannelPointer | undefined {
  return pointers.find((p) => p.app === app && p.channel === channel);
}

function describeKind(
  kind: PromoteActionKind,
  channel: OtaReleaseChannelId,
  currentVc: number | null,
): string {
  if (kind === 'entry') return `First promotion to ${channelLabel(channel)}`;
  if (kind === 'advance') return `Advance to ${channelLabel(channel)}`;
  if (currentVc != null) return `Replace vc ${currentVc} on ${channelLabel(channel)}`;
  return `Set ${channelLabel(channel)} pointer`;
}

/**
 * Release-channel ladder only (beta → general). Signing key does not change the ladder;
 * both standard and platform APKs for a version promote to the same channel together.
 */
export function getPromoteTargetOptions(
  build: Build,
  pointers: ChannelPointer[],
  role: UserRole,
  activeReleaseChannels: OtaReleaseChannelId[],
): PromoteTargetOption[] {
  const app = build.appDir as OtaAppId;
  const ladder = OTA_RELEASE_CHANNEL_LADDER;
  const entry = ladder[0];

  return ladder.map((channel, index) => {
    const ptr = pointerFor(pointers, app, channel);
    const currentVc = ptr?.current ?? null;
    const alreadyLive = activeReleaseChannels.includes(channel);
    const permitted = canDo(role, 'promote', { app, channel });

    let kind: PromoteActionKind = 'replace';
    let enabled = permitted;
    let blockedReason: string | undefined;

    if (!permitted) {
      blockedReason = 'Your role cannot promote to this channel';
    } else if (alreadyLive) {
      kind = 'replace';
      enabled = false;
      blockedReason = 'This build is already live on this channel';
    } else if (index === 0) {
      kind = ptr ? 'replace' : 'entry';
    } else {
      const previousRung = ladder[index - 1];
      const previousPtr = pointerFor(pointers, app, previousRung);
      const buildOnPrevious = activeReleaseChannels.includes(previousRung);

      if (!previousPtr) {
        kind = 'advance';
        enabled = false;
        blockedReason = `Promote to ${channelTableBadgeLabel(entry)} before ${channelTableBadgeLabel(channel)}`;
      } else if (!buildOnPrevious) {
        kind = 'advance';
        enabled = false;
        blockedReason = `This build must be live on ${channelTableBadgeLabel(previousRung)} before advancing`;
      } else {
        kind = ptr ? 'replace' : 'advance';
      }
    }

    return {
      channel,
      kind,
      enabled,
      blockedReason,
      currentVc,
      alreadyLive,
    };
  });
}

export function describePromoteOption(option: PromoteTargetOption): string {
  return describeKind(option.kind, option.channel, option.currentVc);
}

export function hasPromoteTarget(
  build: Build,
  pointers: ChannelPointer[],
  role: UserRole,
  activeReleaseChannels: OtaReleaseChannelId[],
): boolean {
  return getPromoteTargetOptions(build, pointers, role, activeReleaseChannels).some((o) => o.enabled);
}

export function defaultPromoteTarget(options: PromoteTargetOption[]): OtaReleaseChannelId | null {
  const enabled = options.filter((o) => o.enabled);
  if (enabled.length === 0) return null;
  return enabled[enabled.length - 1].channel;
}
